import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../config/supabase';
import { toast } from '../components/ui/use-toast';

const EmailConfirmation = () => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const completeVerification = async () => {
      try {
        // Get the token from URL parameters
        const token = searchParams.get('token');
        const type = searchParams.get('type');
        const verified = searchParams.get('verified') === 'true';

        // If we have a token, verify it with Supabase
        if (token && type === 'signup') {
          const { error: verifyError } = await supabase.auth.verifyOtp({
            token,
            type: 'signup'
          });

          if (verifyError) {
            throw verifyError;
          }
        }

        // Check if we have the email in localStorage from registration.
        // On some privacy-focused browsers (for example Brave with strict cookie/storage blocking),
        // this can fail. In that case we fall back to a normal login and explain what went wrong.
        const email = localStorage.getItem('verification_email');
        const password = localStorage.getItem('temp_password');

        if (!email || !password) {
          console.log('No stored verification credentials found (this may be due to blocked cookies/localStorage)');
          setLoading(false);
          // If we don't have credentials, just show success and guide the user to login manually
          if (!verified) { // Only show toast if not already verified
            toast({
              title: 'Email verified',
              description:
                'Your email has been verified, but we could not keep you signed in. This can happen if your browser blocks cookies or local storage. Please allow cookies for this site and then log in to continue.',
            });
          }
          setTimeout(() => {
            navigate('/login');
          }, 3000);
          return;
        }

        console.log('Found stored credentials, attempting to sign in');

        // Try to sign in with stored credentials
        const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password
        });

        if (signInError) {
          console.error('Sign in error:', signInError);
          throw signInError;
        }

        if (!signInData.user) {
          throw new Error('Sign in successful but no user data returned');
        }

        console.log('Successfully signed in after verification');

        // Update trader's verification status
        const { error: updateError } = await supabase
          .from('traders')
          .update({ 
            verified: true,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', signInData.user.id)
          .select()
          .single();

        if (updateError) {
          console.error('Failed to update trader verification status:', updateError);
          throw updateError;
        }

        // Clear stored credentials
        localStorage.removeItem('verification_email');
        localStorage.removeItem('temp_password');

        console.log('Verification status updated successfully');

        // Only show success message if not already verified
        if (!verified) {
          toast({
            title: 'Success',
            description: 'Email verified! You are now logged in.',
          });
        }

        // Redirect to home page after a short delay
        setTimeout(() => {
          navigate('/');
        }, 2000);

      } catch (err) {
        console.error('Error during verification process:', err);
        setError(err instanceof Error ? err.message : 'Failed to complete verification');
        if (!searchParams.get('verified')) { // Only show error if not already verified
          toast({
            title: 'Verification Process Failed',
            description: 'There was an error completing your verification. Please try logging in.',
            variant: 'destructive',
          });
        }
      } finally {
        setLoading(false);
      }
    };

    completeVerification();
  }, [navigate, searchParams]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="bg-white p-8 rounded-lg shadow-lg text-center max-w-md w-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-6"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Verifying Your Email</h2>
          <p className="text-gray-600">Please wait while we complete your verification...</p>
          <p className="text-sm text-gray-500 mt-4">This will only take a moment</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="bg-white p-8 rounded-lg shadow-lg text-center max-w-md w-full">
          <div className="text-red-500 mb-6">
            <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Verification Failed</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-50">
      <div className="bg-white p-8 rounded-lg shadow-lg text-center max-w-md w-full">
        <div className="text-green-500 mb-6">
          <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Email Verified Successfully!</h2>
        <p className="text-gray-600">You will be redirected shortly...</p>
      </div>
    </div>
  );
};

export default EmailConfirmation;
